/**
 * Invoice Service
 *
 * Handles all invoice-related operations including CRUD,
 * invoice items, and payment recording.
 *
 * @module services/invoiceService
 */

import { supabase } from './supabase';
import { withOverdueDerivation } from '../utils/invoiceHelpers';
// #717 — orden canonico de las lineas: de aca se copian a la factura.
import { ordenarLineas } from './ordenDeLineas';
import { DEFAULT_TAX_RATE, DEFAULT_CURRENCY, QUOTATION_STATUS } from '../utils/constants';
import { validateQuotationReadyToSend } from '../utils/calculations';

/**
 * #629 — Desde qué estados de la cotización es legítimo emitir una factura.
 *
 * Gemela de `QUOTATION_STATUSES_BILLABLE` en
 * `mcp-server/src/tools/invoiceTools.js`. Las dos listas tienen que decir lo
 * mismo: si un carril factura desde `draft` y el otro no, el gate no existe —
 * existe la costumbre de entrar por el carril que no lo tiene.
 *
 * Se permite: `approved`, `sent`, `accepted`.
 * Se bloquea: `draft`, `under_review`, `rejected`.
 *
 * El porqué, explícito para que no haya que reconstruirlo:
 *
 * - **`approved` es la línea.** Aprobar es el paso donde disparan las dos
 *   compuertas que ya existen — la del margen (#593,
 *   `validateQuotationReadyToSend` / `trg_quotation_margin_before_send`) y la
 *   del precio (#609, `assertPricesConfirmed`) — y es donde
 *   `approveQuotation` fija `projects.quoted_amount`. Facturar antes de eso es
 *   cobrar un número que nadie aprobó.
 * - **`sent` y `accepted` son posteriores** a esa línea: el documento ya salió
 *   hacia el cliente con esos números. Es el caso normal de facturación.
 * - **`draft` y `under_review` son anteriores.** Son los estados donde el
 *   borrador todavía se está armando; el margen `NULL` es su estado normal
 *   porque el RACI de Jorge lo pone en el paso 3. Facturar ahí es exactamente
 *   el bug de #629.
 * - **`rejected` es un "no" del cliente.** Cobrar por algo rechazado no es un
 *   descuido de proceso, es un error de negocio.
 */
export const QUOTATION_STATUSES_BILLABLE = [
  QUOTATION_STATUS.APPROVED,
  QUOTATION_STATUS.SENT,
  QUOTATION_STATUS.ACCEPTED,
];

/**
 * List invoices with optional filters
 * @param {Object} filters - Filter options
 * @param {string} filters.projectId - Filter by project
 * @param {string} filters.status - Filter by status
 * @param {string} filters.clientId - Filter by client
 * @returns {Promise<{data, error}>}
 */
export const listInvoices = async (filters = {}) => {
  try {
    // 'overdue' is a derived status (see src/utils/invoiceHelpers.js) —
    // the stored `status` column never transitions to it automatically
    // (no cron job / trigger), so filtering with .eq('status', 'overdue')
    // would always return zero rows (issue #278). Fetch candidates without
    // the raw status filter instead and derive overdue after the fetch.
    const isOverdueFilter = filters.status === 'overdue';

    let query = supabase
      .from('invoices')
      .select(`
        *,
        project:projects(id, project_number, title, client_id, client:clients!projects_client_id_fkey(id, company_name))
      `, { count: 'exact' })
      .order('created_at', { ascending: false });

    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }

    if (filters.status && !isOverdueFilter) {
      query = query.eq('status', filters.status);
    }

    if (filters.clientId) {
      query = query.eq('client_id', filters.clientId);
    }

    const { data, count, error } = await query;

    if (error) throw error;

    // Derive is_overdue/effective_status for every row (mirrors the MCP
    // get_invoice/list_invoices tools), then apply the overdue filter
    // client-side if that's what was requested.
    //
    // `client` de primer nivel: la lista (/facturas) lee `inv.client` para la
    // columna Cliente y para el buscador, y ese campo no existía —la columna
    // decía "-" siempre y buscar por nombre de cliente no encontraba nada.
    //
    // No se puede embeber `client:clients(...)` por `invoices.client_id`
    // porque esa columna NO tiene foreign key hacia `clients` (verificado por
    // pg_constraint en prod: `invoices` sólo tiene FK a `projects` y a
    // `profiles`), y sin FK PostgREST no sabe resolver el embed. El cliente
    // sale entonces del proyecto, que sí está enlazado — y en producción
    // `invoices.client_id` coincide con `projects.client_id` en el 100% de
    // las filas. Si algún día se necesita que difieran, hay que crear la FK
    // primero (ver reporte).
    let invoices = (data || []).map((inv) => ({
      ...withOverdueDerivation(inv),
      client: inv.project?.client || null,
    }));
    if (isOverdueFilter) {
      invoices = invoices.filter((inv) => inv.is_overdue);
    }

    // Get payment totals for each invoice
    if (invoices.length > 0) {
      const invoiceIds = invoices.map(inv => inv.id);
      const { data: payments, error: paymentsError } = await supabase
        .from('invoice_payments')
        .select('invoice_id, amount')
        .in('invoice_id', invoiceIds);

      if (!paymentsError && payments) {
        const paymentsByInvoice = {};
        payments.forEach(p => {
          paymentsByInvoice[p.invoice_id] = (paymentsByInvoice[p.invoice_id] || 0) + p.amount;
        });

        invoices.forEach(invoice => {
          invoice.paid_amount = paymentsByInvoice[invoice.id] || 0;
          invoice.balance = (invoice.amount || 0) - invoice.paid_amount;
        });
      }
    }

    return { data: invoices, count: isOverdueFilter ? invoices.length : count, error: null };
  } catch (error) {
    console.error('List invoices error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * Get a single invoice with items and payments
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<{data, error}>}
 */
export const getInvoice = async (invoiceId) => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        project:projects(id, project_number, title, client_id, client:clients!projects_client_id_fkey(id, company_name, legal_id, primary_contact_email, primary_contact_phone, address_line1)),
        created_by_profile:profiles!invoices_created_by_fkey(id, full_name)
      `)
      .eq('id', invoiceId)
      .single();

    if (error) throw error;

    // Get invoice items
    const { data: items, error: itemsError } = await supabase
      .from('invoice_items')
      .select('*')
      .eq('invoice_id', invoiceId)
      .order('sort_order', { ascending: true });

    if (itemsError) throw itemsError;

    // Get payments
    const { data: payments, error: paymentsError } = await supabase
      .from('invoice_payments')
      .select(`
        *,
        created_by_profile:profiles!invoice_payments_created_by_fkey(id, full_name)
      `)
      .eq('invoice_id', invoiceId)
      .order('payment_date', { ascending: false });

    if (paymentsError) throw paymentsError;

    const paidAmount = payments?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;

    return {
      data: {
        ...withOverdueDerivation(data),
        // Mismo alias que en listInvoices, para que el detalle y la lista
        // hablen del mismo campo (InvoiceDetail hoy lee project.client).
        client: data.project?.client || null,
        items: items || [],
        payments: payments || [],
        paid_amount: paidAmount,
        balance: (data.amount || 0) - paidAmount,
      },
      error: null,
    };
  } catch (error) {
    console.error('Get invoice error:', error);
    return { data: null, error };
  }
};

/**
 * Create a new invoice
 * @param {Object} invoiceData - Invoice data
 * @param {Array} items - Invoice items array
 * @returns {Promise<{data, error}>}
 */
export const createInvoice = async (invoiceData, items = []) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data: invoice, error: invoiceError } = await supabase
      .from('invoices')
      .insert({
        ...invoiceData,
        created_by: user.id,
      })
      .select()
      .single();

    if (invoiceError) throw invoiceError;

    // Insert items if provided
    if (items.length > 0) {
      const itemsWithInvoiceId = items.map((item, idx) => ({
        ...item,
        invoice_id: invoice.id,
        sort_order: idx,
      }));

      const { error: itemsError } = await supabase
        .from('invoice_items')
        .insert(itemsWithInvoiceId);

      if (itemsError) throw itemsError;

      // #558 — RELEER, y devolver esto. Gemelo web del re-read en
      // invoiceTools.generateInvoice.
      //
      // `invoice` es la fila de ANTES de insertar los renglones. El trigger
      // `recalculate_invoice_total` corre despues, por cada item, y reescribe
      // subtotal/tax_amount/amount. Devolver la fila vieja es lo que hacia el
      // bug invisible: la GUI mostraba el total correcto mientras la base
      // guardaba otro, asi que nadie podia notarlo mirando la pantalla.
      const { data: persisted, error: rereadError } = await supabase
        .from('invoices')
        .select('*')
        .eq('id', invoice.id)
        .single();

      if (rereadError) throw rereadError;

      return { data: persisted, error: null };
    }

    return { data: invoice, error: null };
  } catch (error) {
    console.error('Create invoice error:', error);
    return { data: null, error };
  }
};

/**
 * Update an invoice
 * @param {string} invoiceId - Invoice ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{data, error}>}
 */
export const updateInvoice = async (invoiceId, updates) => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .update(updates)
      .eq('id', invoiceId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Update invoice error:', error);
    return { data: null, error };
  }
};

/**
 * Add an item to an invoice
 * @param {Object} itemData - Item data including invoice_id
 * @returns {Promise<{data, error}>}
 */
export const addInvoiceItem = async (itemData) => {
  try {
    const { data, error } = await supabase
      .from('invoice_items')
      .insert(itemData)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Add invoice item error:', error);
    return { data: null, error };
  }
};

/**
 * Delete an invoice item
 * @param {string} itemId - Item ID
 * @returns {Promise<{success, error}>}
 */
export const deleteInvoiceItem = async (itemId) => {
  try {
    const { error } = await supabase
      .from('invoice_items')
      .delete()
      .eq('id', itemId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('Delete invoice item error:', error);
    return { success: false, error };
  }
};

/**
 * Record a payment against an invoice
 * @param {Object} paymentData - Payment data
 * @returns {Promise<{data, error}>}
 */
export const recordPayment = async (paymentData) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase
      .from('invoice_payments')
      .insert({
        ...paymentData,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Record payment error:', error);
    return { data: null, error };
  }
};

/**
 * Delete an invoice
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<{success, error}>}
 */
export const deleteInvoice = async (invoiceId) => {
  try {
    const { error } = await supabase
      .from('invoices')
      .delete()
      .eq('id', invoiceId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('Delete invoice error:', error);
    return { success: false, error };
  }
};

/**
 * Generate invoice from a quotation
 * @param {string} projectId - Project ID
 * @param {string} quotationId - Quotation ID
 * @param {Object} options - Additional invoice options
 * @returns {Promise<{data, error}>}
 */
export const generateFromQuotation = async (projectId, quotationId, options = {}) => {
  try {
    // Get quotation with items
    // #717 — orden explícito: estas líneas se copian a la factura, o sea al
    // documento que ve el cliente. Ver `services/ordenDeLineas.js`.
    const { data: quotation, error: quotationError } = await ordenarLineas(
      supabase
        .from('quotations')
        .select(`
          *,
          quotation_items(*)
        `)
        .eq('id', quotationId)
    ).single();

    if (quotationError) throw quotationError;

    // #629 — La compuerta que faltaba. Hasta acá la facturación esquivaba las
    // DOS que #593 dejó montadas (`validateQuotationReadyToSend` en los
    // handlers y `trg_quotation_margin_before_send` en la base): leía el
    // margen, lo coaccionaba a 0 más abajo (`?? 0`) y emitía. Un margen `NULL`
    // no es "se cotiza a costo", es "nadie lo definió todavía" — y es el
    // estado NORMAL de una cotización recién creada. El documento que salía de
    // ahí iba al cliente facturado al costo.
    //
    // El orden importa: primero el estado, después el margen. Un borrador se
    // rechaza por ser borrador aunque le falte además el margen; decirle
    // "falta el % de riesgo" a alguien cuya cotización ni siquiera está
    // aprobada lo manda a llenar un campo que igual no lo va a destrabar.
    if (!QUOTATION_STATUSES_BILLABLE.includes(quotation.status)) {
      throw new Error(
        `No se puede facturar una cotización en estado "${quotation.status}". ` +
        `Sólo se factura desde ${QUOTATION_STATUSES_BILLABLE.join(', ')}: la ` +
        'aprobación es donde se decide el margen y se fija el monto del ' +
        'proyecto. Aprobá la cotización primero.'
      );
    }

    // #629 — Gemela de `assertMarginDecided` en
    // `mcp-server/src/tools/invoiceTools.js`. Un 0 deliberado pasa (cotizar a
    // costo es una decisión que alguien puede tomar); `NULL` no.
    const readiness = validateQuotationReadyToSend(quotation);
    if (!readiness.isValid) {
      throw new Error(`No se puede facturar esta cotización. ${readiness.message}`);
    }

    // Get project for client info
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select('client_id')
      .eq('id', projectId)
      .single();

    if (projectError) throw projectError;

    // Generate invoice number
    const invoiceNumber = `INV-${Date.now().toString(36).toUpperCase()}`;

    const invoiceDate = options.invoice_date || new Date().toISOString().split('T')[0];
    const paymentTerms = options.payment_terms || 30;
    const dueDate = new Date(invoiceDate);
    dueDate.setDate(dueDate.getDate() + paymentTerms);

    const invoiceData = {
      project_id: projectId,
      quotation_id: quotationId,
      client_id: project.client_id,
      invoice_number: invoiceNumber,
      invoice_date: invoiceDate,
      due_date: dueDate.toISOString().split('T')[0],
      subtotal: quotation.subtotal || 0,
      // #457: `||` promovia un 0% exento a 13% al facturar. Es el gemelo web
      // de invoiceTools.js:154 -- mientras uno de los dos siguiera con `||`,
      // el bug vivia igual segun por donde se generara la factura.
      tax_rate: quotation.tax_rate ?? DEFAULT_TAX_RATE,
      tax_amount: quotation.tax_amount || 0,
      amount: quotation.total || 0,
      // #558 — gemelo web de invoiceTools.js. Riesgo y ganancia son terminos
      // del trato, no renglones: sin copiarlos, el trigger
      // `recalculate_invoice_total` reescribe `amount` como subtotal + IVA y la
      // factura sale 24% corta. Este carril es el que Jorge usa a diario desde
      // la GUI, asi que arreglar solo el MCP habria dejado el bug vivo justo
      // por donde mas se factura.
      //
      // `?? 0` y no `|| 0`: un margen de 0 es deliberado (obra al costo) y debe
      // sobrevivir igual que el 0% de IVA de #457 en la linea de arriba.
      //
      // #629: el `?? 0` ya NO puede ver un `null` — la compuerta de arriba
      // corta antes. Se queda como red de seguridad para el CHECK
      // `invoices_valid_percentages` (la columna en `invoices` es NOT NULL),
      // no como política: la política es que un `null` no llega hasta acá.
      risk_percentage: quotation.risk_percentage ?? 0,
      profit_percentage: quotation.profit_percentage ?? 0,
      // #558: la GUI no mandaba moneda, asi que caia en el DEFAULT roto de la
      // columna (el literal `'USD'` CON comillas). ProMedic cotiza en colones.
      currency: DEFAULT_CURRENCY,
      payment_terms: paymentTerms,
      status: 'draft',
      notes: options.notes || '',
    };

    // Map quotation items to invoice items
    const items = (quotation.quotation_items || []).map((qi, idx) => ({
      description: qi.description,
      item_type: qi.item_type === 'tool' ? 'other' : (qi.item_type || 'service'),
      quantity: qi.quantity,
      unit: qi.unit,
      unit_price: qi.unit_price,
      quotation_item_id: qi.id,
      sort_order: idx,
    }));

    return await createInvoice(invoiceData, items);
  } catch (error) {
    console.error('Generate from quotation error:', error);
    return { data: null, error };
  }
};
